import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import CookieSettingsContainer from "web/react/components/cookie-settings-container/cookie-settings-container";
import { Button } from "web/react/emo/button/button";
import { Text } from "web/react/emo/text";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import { consentToAllCookies, rejectAllNonEssentialCookies } from "web/redux/ducks/cookie-consent";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { getHtmlProps } from "web/script/modules/html-tag-props";
import { getUrl } from "web/script/routing/getUrl";
import history from "web/script/utils/history";
import * as styles from "./cookie-consent-banner.css";
import { COOKIE_SETTINGS_OVERLAY, useCookieConsentPending } from "./use-cookie-consent-pending";

export default function CookieConsentBanner(): JSX.Element | null {
    const dispatch = useDispatch();
    const showBanner = useCookieConsentPending();
    const settingsModalIsOpen = Boolean(useOverlayWatcher(COOKIE_SETTINGS_OVERLAY));

    function onSettingsModalClose(): void {
        history.popOverlay(COOKIE_SETTINGS_OVERLAY);
    }

    function onSettingsModalOpen(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay != COOKIE_SETTINGS_OVERLAY) {
            history.pushOverlay(COOKIE_SETTINGS_OVERLAY);
        }
    }

    function onAcceptButtonClick(): void {
        dispatch(consentToAllCookies());
        analytics.event("Cookie consent", "Clicked", "Hide");
    }

    function onRejectAllButtonClick(): void {
        dispatch(rejectAllNonEssentialCookies());
        analytics.event("Cookie consent", "clicked", "accept_required");
    }

    useEffect(() => {
        if (showBanner) {
            analytics.event("Cookie consent", "Shown", undefined, true);
        }
    }, [showBanner]);

    const descriptionSafeHtml = {
        __html: gettext("cookie_notification.banner.description", {
            policy_link_props: getHtmlProps({
                href: getUrl("helpCookiePolicy"),
            }),
        }),
    };

    return (
        <>
            {showBanner && (
                <section
                    className={styles.banner}
                    aria-label={gettext("cookie_notification.banner.title")}
                >
                    <div className={styles.inner}>
                        <div className={styles.text}>
                            <div className={styles.copy}>
                                <Text as="p" textStyle="body-s-bold">
                                    {gettext("cookie_notification.banner.title")}
                                </Text>
                                <Text as="p" textStyle="body-s">
                                    <span dangerouslySetInnerHTML={descriptionSafeHtml} />
                                </Text>
                            </div>
                            <div className={styles.purposes}>
                                <Text as="p" textStyle="body-s-bold">
                                    {gettext("cookie_notification.banner.purposes_title")}
                                </Text>
                                <Text as="p" textStyle="body-s">
                                    {gettext("cookie_notification.banner.purposes")}
                                </Text>
                            </div>
                        </div>
                        <div className={styles.buttons}>
                            <Button
                                variant="primary"
                                width="full"
                                onClick={onAcceptButtonClick}
                                dataTestId="cookie-accept-button"
                                title={gettext("general.accept")}
                            />
                            <Button
                                variant="primary"
                                width="full"
                                onClick={onRejectAllButtonClick}
                                dataTestId="cookie-reject-button"
                                title={gettext(
                                    "cookie_notification.cookie_settings.accept_required.link"
                                )}
                            />
                            <Button
                                variant="secondary"
                                width="full"
                                onClick={onSettingsModalOpen}
                                dataTestId="cookie-settings-button"
                                title={gettext("cookie_notification.modal.customize_cta")}
                            />
                        </div>
                    </div>
                </section>
            )}
            <CookieSettingsContainer
                isOpen={settingsModalIsOpen}
                onModalClose={onSettingsModalClose}
            />
        </>
    );
}
